/**
 * Paragraph IR builder — converts <w:p> elements to ParagraphIR nodes.
 *
 * Resolves style chain, detects headings, extracts paragraph properties,
 * and delegates inline content to runIRBuilder.
 */

import type { ParagraphIR, InlineIR, BlockIR } from "../irTypes";
import type { ParseContext } from "../../ooxmlParser/types";
import { getChildNS, W_NS } from "../../ooxmlParser/zipReader";
import { detectHeadingLevel } from "../../ooxmlParser/context";
import {
  resolveStylePropertiesRaw,
  parseParagraphPropertiesRaw,
  mergeParagraphProperties,
} from "./styleResolver";
import { buildRunIR } from "./runIRBuilder";
import { convertOmmlToLatex } from "../../ooxmlParser/mathParser";
import { CommentTracker } from "../../ooxmlParser/commentParser";
import {
  parseInsertion,
  parseDeletion,
  TrackChangeTracker,
} from "../../ooxmlParser/trackChangeParser";
import { parseRun as v1ParseRun } from "../../ooxmlParser/runParser";

/**
 * Parse a <w:p> element into a BlockIR node.
 * Returns HorizontalRuleIR for horizontal line paragraphs, otherwise ParagraphIR.
 */
export function buildParagraphIR(pEl: Element, ctx: ParseContext): BlockIR {
  // Extract <w:pPr>
  const pPrEl = getChildNS(pEl, W_NS, "pPr");
  const explicitPProps = pPrEl ? parseParagraphPropertiesRaw(pPrEl) : {};

  // Detect horizontal rules: empty paragraph with bottom border from <w:pBdr>
  if (pPrEl && isHorizontalRule(pPrEl, pEl)) {
    return { kind: "horizontalRule" };
  }

  // Resolve style chain
  const styleId = explicitPProps.styleId;
  const resolvedStyle = styleId
    ? resolveStylePropertiesRaw(styleId, ctx)
    : { runProperties: {}, paragraphProperties: {} };

  // Merge style paragraph props + explicit paragraph props
  const mergedPProps = mergeParagraphProperties(
    resolvedStyle.paragraphProperties,
    explicitPProps
  );

  // Resolve numbering first (direct numPr or style-linked) — heading detection
  // needs it to avoid promoting numbered clauses to headings via outlineLvl.
  let numId = mergedPProps.numId;
  let numLevel = mergedPProps.numLevel;
  if (!numId && styleId) {
    const styleNumId = ctx.styleNumIdMap.get(styleId);
    if (styleNumId) {
      numId = styleNumId;
      numLevel = numLevel ?? 0;
    }
  }

  // Detect heading
  // Re-use V1's detectHeadingLevel (it works on V1's ParagraphProperties which has the same field names we need).
  // Use the DIRECT (explicit) outlineLevel, not the style-inherited one: a custom
  // paragraph style that sets outlineLvl is using it for TOC/navigation, not to
  // render as a heading (e.g. legal "MF Para subclause" / "-nonum" clause styles).
  // Explicit Heading 1-6 styles are still detected via styleId/name in detectHeadingLevel.
  const v1PProps = {
    styleId: explicitPProps.styleId,
    outlineLevel: explicitPProps.outlineLevel,
  };
  const headingLevel = detectHeadingLevel(styleId, v1PProps, ctx, !!numId);

  // Get the run-level style defaults (for stripping redundant marks on runs).
  //
  // IMPORTANT: only the paragraph *style*'s run properties belong here. We must
  // NOT fold in <w:pPr><w:rPr> — those are the paragraph-MARK (pilcrow) run
  // properties, not part of the run-property inheritance chain. Word routinely
  // mirrors a run's formatting into the paragraph-mark rPr (e.g. bolding a whole
  // paragraph writes <w:b/> in both the run rPr and the paragraph-mark rPr).
  // Since this baseline is used ONLY to strip "redundant" marks — and v2 never
  // applies paragraph-mark properties to the rendered runs — folding it in would
  // strip the run's explicit bold/italic/etc. as redundant and silently drop the
  // formatting from the output. (Regression: tdf75573_lostTable, where a bold
  // list-item lost its bold because the paragraph-mark rPr also declared <w:b/>.)
  const styleRunProps = { ...resolvedStyle.runProperties };

  // Parse inline content
  const isHeading = !!headingLevel;
  const inlines = parseParagraphContent(pEl, ctx, styleRunProps, isHeading);

  // Check for section break
  let hasSectBreak = false;
  if (pPrEl) {
    const sectPr = getChildNS(pPrEl, W_NS, "sectPr");
    if (sectPr) hasSectBreak = true;
  }

  // Build ParagraphIR
  const para: ParagraphIR = {
    kind: "paragraph",
    inlines,
  };

  if (headingLevel) {
    para.headingLevel = headingLevel as 1 | 2 | 3 | 4 | 5 | 6;
  }

  if (mergedPProps.alignment) para.alignment = mergedPProps.alignment;
  if (mergedPProps.spaceBeforeTwips)
    para.spaceBeforeTwips = mergedPProps.spaceBeforeTwips;
  if (mergedPProps.spaceAfterTwips)
    para.spaceAfterTwips = mergedPProps.spaceAfterTwips;
  if (mergedPProps.lineSpacing) para.lineSpacing = mergedPProps.lineSpacing;
  if (mergedPProps.leftIndentTwips)
    para.leftIndentTwips = mergedPProps.leftIndentTwips;
  if (mergedPProps.rightIndentTwips)
    para.rightIndentTwips = mergedPProps.rightIndentTwips;
  if (mergedPProps.firstLineIndentTwips)
    para.firstLineIndentTwips = mergedPProps.firstLineIndentTwips;

  if (numId) {
    // Resolve list info from numbering definitions
    const numDef = ctx.numbering.get(numId);
    const level = numLevel ?? 0;
    if (numDef) {
      const levelDef = numDef.levels.find(l => l.level === level);
      if (levelDef) {
        const isOutlineNumbering = /%.+%/.test(levelDef.levelText); // Multiple % references
        para.list = {
          numId,
          level,
          numFmt: levelDef.numFmt,
          levelText: levelDef.levelText,
          start: levelDef.start,
          indentTwips: levelDef.indent
            ? Math.round(levelDef.indent * 20)
            : undefined, // V1 stores indent in pts
          isOutlineNumbering,
        };
      }
    }
  }

  if (hasSectBreak) para.hasSectBreak = true;
  if (styleId) para.sourceStyleId = styleId;

  return para;
}

/**
 * Parse the inline content of a paragraph element.
 * Handles <w:r>, <w:hyperlink>, <w:sdt>, <m:oMath>, <m:oMathPara>, and comment/track change elements.
 */
function parseParagraphContent(
  parentEl: Element,
  ctx: ParseContext,
  styleRunProps: import("../irTypes").ResolvedRunProperties,
  isHeading: boolean = false
): InlineIR[] {
  const inlines: InlineIR[] = [];
  const children = parentEl.childNodes;

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType !== 1) continue;

    const el = child as Element;
    const localName = el.localName || el.nodeName.replace(/^[a-z]+:/, "");
    const nsPrefix = el.nodeName.includes(":") ? el.nodeName.split(":")[0] : "";

    switch (localName) {
      case "r": {
        // Check if comment tracker has active comments
        const commentTracker = ctx.commentTracker as CommentTracker | undefined;
        const commentMarks = commentTracker
          ? commentTracker.getActiveCommentMarks()
          : [];

        if (commentMarks.length > 0) {
          // Delegate to V1's parseRun when comments are active, so comment marks are applied
          const v1Nodes = v1ParseRun(el, ctx);
          for (const v1Node of v1Nodes) {
            if (v1Node.type === "text") {
              v1Node.marks = [...(v1Node.marks || []), ...commentMarks];
              // Record text for commentedText metadata
              if (v1Node.text && commentTracker) {
                commentTracker.recordText(v1Node.text);
              }
            }
            inlines.push({ kind: "passthroughInline", pmNode: v1Node });
          }
        } else {
          const runInlines = buildRunIR(el, styleRunProps, isHeading, ctx);
          inlines.push(...runInlines);
        }
        break;
      }

      case "hyperlink": {
        // Resolve href from relationship ID
        const rId =
          el.getAttributeNS(
            "http://schemas.openxmlformats.org/officeDocument/2006/relationships",
            "id"
          ) || el.getAttribute("r:id");
        let href = "";
        if (rId) {
          const rel = ctx.relationships.get(rId);
          if (rel) href = rel.target;
        }
        // Also check w:anchor for internal bookmarks
        if (!href) {
          const anchor =
            el.getAttributeNS(W_NS, "anchor") || el.getAttribute("w:anchor");
          if (anchor) href = `#${anchor}`;
        }

        if (href) {
          inlines.push({ kind: "hyperlinkStart", href });
        }
        // Parse hyperlink children (runs)
        const hyperlinkInlines = parseParagraphContent(
          el,
          ctx,
          styleRunProps,
          isHeading
        );
        inlines.push(...hyperlinkInlines);
        if (href) {
          inlines.push({ kind: "hyperlinkEnd" });
        }
        break;
      }

      case "sdt": {
        // Structured Document Tag — unwrap and parse content
        const sdtContent =
          getChildNS(el, W_NS, "sdtContent") ||
          getChildNS(el, el.namespaceURI || "", "sdtContent");
        if (sdtContent) {
          const sdtInlines = parseParagraphContent(
            sdtContent,
            ctx,
            styleRunProps,
            isHeading
          );
          inlines.push(...sdtInlines);
        }
        break;
      }

      case "ins": {
        // Track change: insertion — delegate to V1's parseInsertion which handles marks
        const trackChangeTracker = ctx.trackChangeTracker as
          | TrackChangeTracker
          | undefined;
        if (trackChangeTracker) {
          const insNodes = parseInsertion(el, ctx, trackChangeTracker);
          for (const pmNode of insNodes) {
            inlines.push({ kind: "passthroughInline", pmNode });
          }
        } else {
          const insInlines = parseParagraphContent(
            el,
            ctx,
            styleRunProps,
            isHeading
          );
          inlines.push(...insInlines);
        }
        break;
      }

      case "del": {
        // Track change: deletion — delegate to V1's parseDeletion which handles marks
        const trackChangeTracker2 = ctx.trackChangeTracker as
          | TrackChangeTracker
          | undefined;
        if (trackChangeTracker2) {
          const delNodes = parseDeletion(el, ctx, trackChangeTracker2);
          for (const pmNode of delNodes) {
            inlines.push({ kind: "passthroughInline", pmNode });
          }
        } else {
          const delInlines = parseParagraphContent(
            el,
            ctx,
            styleRunProps,
            isHeading
          );
          inlines.push(...delInlines);
        }
        break;
      }

      // Tracked MOVES — moveTo = destination (insertion-style), moveFrom =
      // source (deletion-style). Without these the moved text was dropped on
      // import (V2 is the active pipeline).
      case "moveTo": {
        const moveTracker = ctx.trackChangeTracker as
          | TrackChangeTracker
          | undefined;
        if (moveTracker) {
          const moveNodes = parseInsertion(el, ctx, moveTracker);
          for (const pmNode of moveNodes) {
            inlines.push({ kind: "passthroughInline", pmNode });
          }
        } else {
          inlines.push(
            ...parseParagraphContent(el, ctx, styleRunProps, isHeading)
          );
        }
        break;
      }

      case "moveFrom": {
        const moveTracker2 = ctx.trackChangeTracker as
          | TrackChangeTracker
          | undefined;
        if (moveTracker2) {
          const moveNodes = parseDeletion(el, ctx, moveTracker2);
          for (const pmNode of moveNodes) {
            inlines.push({ kind: "passthroughInline", pmNode });
          }
        }
        // No tracker — moved-from text omitted (it lives at the destination).
        break;
      }

      case "oMath": {
        // Inline math
        if (nsPrefix === "m" || el.namespaceURI?.includes("math")) {
          try {
            const latex = convertOmmlToLatex(el);
            if (latex) {
              inlines.push({ kind: "inlineMath", latex });
            }
          } catch {
            // Skip malformed math
          }
        }
        break;
      }

      case "oMathPara": {
        // Display math (block math placeholder — split in Stage 2)
        if (nsPrefix === "m" || el.namespaceURI?.includes("math")) {
          // Find <m:oMath> inside
          const oMathChildren = el.getElementsByTagNameNS(
            el.namespaceURI ||
              "http://schemas.openxmlformats.org/officeDocument/2006/math",
            "oMath"
          );
          for (let j = 0; j < oMathChildren.length; j++) {
            try {
              const latex = convertOmmlToLatex(oMathChildren[j]);
              if (latex) {
                inlines.push({ kind: "blockMathPlaceholder", latex });
              }
            } catch {
              // Skip malformed math
            }
          }
        }
        break;
      }

      case "commentRangeStart": {
        // Forward to V1's CommentTracker
        const ct = ctx.commentTracker as CommentTracker | undefined;
        if (ct) {
          const id =
            el.getAttributeNS(W_NS, "id") || el.getAttribute("w:id") || "";
          if (id) ct.handleCommentRangeStart(id);
        }
        break;
      }

      case "commentRangeEnd": {
        const ct2 = ctx.commentTracker as CommentTracker | undefined;
        if (ct2) {
          const id =
            el.getAttributeNS(W_NS, "id") || el.getAttribute("w:id") || "";
          if (id) ct2.handleCommentRangeEnd(id);
        }
        break;
      }

      case "commentReference":
        // Handled inside runIRBuilder via V1's CommentTracker
        break;

      case "bookmarkStart":
      case "bookmarkEnd":
        // Ignored
        break;

      case "pPr":
        // Already processed, skip
        break;

      default:
        // Skip unknown elements
        break;
    }
  }

  return inlines;
}

/**
 * Detect if a paragraph represents a horizontal rule.
 * In OOXML, horizontal lines are typically:
 * 1. An empty paragraph with <w:pBdr><w:bottom> border
 * 2. A paragraph with a "HorizontalRule" style ID (or similar naming)
 */
function isHorizontalRule(pPrEl: Element, pEl: Element): boolean {
  // Check style-based horizontal rules
  const pStyleEl = getChildNS(pPrEl, W_NS, "pStyle");
  if (pStyleEl) {
    const styleId =
      pStyleEl.getAttributeNS(W_NS, "val") ||
      pStyleEl.getAttribute("w:val") ||
      "";
    const lowerStyle = styleId.toLowerCase();
    if (
      lowerStyle.includes("horizontalrule") ||
      lowerStyle.includes("horizontal")
    ) {
      return true;
    }
  }

  // Check for <w:pBdr> with a bottom border on a paragraph with no visible text
  const pBdr = getChildNS(pPrEl, W_NS, "pBdr");
  if (pBdr) {
    const bottom = getChildNS(pBdr, W_NS, "bottom");
    if (bottom) {
      // Check border style is visible (not "none" or "nil")
      const val =
        bottom.getAttributeNS(W_NS, "val") ||
        bottom.getAttribute("w:val") ||
        "";
      if (val && val !== "none" && val !== "nil") {
        // Verify no <w:t> or <w:delText> elements exist (no visible text)
        const textEls = pEl.getElementsByTagNameNS(W_NS, "t");
        const delTextEls = pEl.getElementsByTagNameNS(W_NS, "delText");
        const hasText =
          Array.from(textEls).some(
            t => (t.textContent || "").trim().length > 0
          ) ||
          Array.from(delTextEls).some(
            t => (t.textContent || "").trim().length > 0
          );
        if (!hasText) {
          return true;
        }
      }
    }
  }

  return false;
}
